import { catalogApiRef, EntityRefLink, useEntity } from '@backstage/plugin-catalog-react';
import { Grid, Box, LinearProgress, Typography, Card, CardHeader, CardContent, Button, CardActions, makeStyles } from '@material-ui/core';
import { projectPageTranslationRef } from '../../translation/project-page/project-page';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { useApi } from '@backstage/core-plugin-api';
import useAsync from 'react-use/lib/useAsync';

const useStyles = makeStyles(theme => ({
    card: {
        height: '100%',
        borderRadius: 12,
        border: `1px solid ${theme.palette.divider}`,
        transition: '0.2s',
        display: 'flex',
        flexDirection: 'column',

        '&:hover': {
            boxShadow: theme.shadows[4],
            transform: 'translateY(-2px)',
        },
    },

    header: {
        color: theme.getPageTheme({ themeId: 'Proyecto' }).fontColor,
        padding: theme.spacing(2, 2, 3),
        backgroundImage: theme.getPageTheme({ themeId: 'Proyecto' }).backgroundImage,
        backgroundPosition: 0,
        backgroundSize: 'inherit',
    },

    content: {
        flexGrow: 1,
    },

    actions: {
        paddingLeft: theme.spacing(2),
        paddingBottom: theme.spacing(2),
    },

    description: {
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        minHeight: 50,
    },
}));

export const ProjectSubProjects = () => {
    const classes = useStyles();
    const { entity } = useEntity();
    const { t } = useTranslationRef(projectPageTranslationRef);
    const catalogApi = useApi(catalogApiRef);

    // Extract relevant fields from the project entity
    const projectName = entity.metadata.name;
    const entityRef = `project:${entity.metadata.namespace}/${projectName}`;

    const { loading, error, value: children } = useAsync(async () => {
        const all = await catalogApi.getEntities({
            filter: { kind: 'Project' },
        });

        return all.items.filter(e => e.spec?.parent === entityRef);
    }, [entityRef, catalogApi]);

    return (
        <Box mt={3}>
            {!loading && !error && (
                <>
                    <Typography variant="h5" gutterBottom>
                        {t('projectForm.childProjects')}
                    </Typography>
                    {console.log('📦 children:', children)}

                    <Grid container spacing={3}>
                        {children?.map((proj: any) => (
                            <Grid item xs={12} sm={6} md={4} xl={3} key={proj.metadata.uid}>
                                <Card className={classes.card}>
                                    <CardHeader
                                        className={classes.header}
                                        title={
                                            <Box>
                                                {proj.spec?.type && (
                                                    <Typography variant="subtitle2" component="h3">
                                                        {proj.spec?.type}
                                                    </Typography>
                                                )}
                                                {proj.metadata.title && (
                                                    <Typography variant="h6" component="h4">
                                                        {`${proj.spec?.number} - ${proj.metadata.title}`}
                                                    </Typography>
                                                )}
                                            </Box>
                                        }
                                    />
                                    <CardContent>

                                        <Grid container spacing={1}>

                                            {/* Project Manager */}
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2" color="textSecondary">{t('projectTableColumns.projectManager')}</Typography>
                                                {proj.spec?.projectManager ? <EntityRefLink
                                                    entityRef={proj.spec?.projectManager as string}
                                                    defaultKind={'User'}
                                                    defaultNamespace="default"
                                                /> : <Typography variant="body1">
                                                    {proj.spec?.projectManager as string || '—'}
                                                </Typography>}
                                            </Grid>

                                            {/* Execution Type */}
                                            <Grid item xs={12}>
                                                <Typography variant="body2" color="textSecondary">{t('projectTableColumns.executionType')}</Typography>
                                                <Typography variant="body1">{proj.spec?.executionType as string || '—'}</Typography>
                                            </Grid>

                                            {/* Ciclo de Vida */}
                                            <Grid item xs={12}>
                                                <Typography variant="body2" color="textSecondary">
                                                    {t('projectTableColumns.lifecycle')}
                                                </Typography>
                                                <Typography variant="body1">
                                                    {proj.spec?.lifecycle ?? '—'}
                                                </Typography>
                                            </Grid>

                                            {/* Percent Completed con barra */}
                                            <Grid item xs={12}>
                                                <Typography variant="body2" color="textSecondary">{t('projectTableColumns.percentCompleted')}</Typography>
                                                <Box display="flex" alignItems="center">
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={proj.spec?.percentCompleted as number}
                                                        style={{ flexGrow: 1, marginRight: 8 }}
                                                    />
                                                    <Typography variant="body1"><strong>{proj.spec?.percentCompleted as string}%</strong></Typography>
                                                </Box>
                                            </Grid>

                                        </Grid>

                                    </CardContent>

                                    <CardActions className={classes.actions}>
                                        <Button
                                            size="small"
                                            color="primary"
                                            component="a"
                                            href={`/catalog/${proj.metadata.namespace ?? 'default'}/${proj.kind.toLowerCase()}/${proj.metadata.name}`}
                                        >
                                            {t('projectApplications.moreDetails')}
                                        </Button>
                                    </CardActions>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </>
            )}
        </Box>
    );
};